// Auto-recovery from stale-bundle-after-deploy crashes (Task #3081).
//
// Every page in the app is a content-hashed chunk loaded on demand via
// React.lazy(() => import(...)). When a new version is deployed, those chunk
// filenames change. A browser tab that was already open is still running the
// OLD bundle, which points at chunk names that no longer exist on the server.
// The moment that tab navigates to a not-yet-loaded lazy route, the dynamic
// import 404s and rejects — surfacing as a ChunkLoadError / "Failed to fetch
// dynamically imported module" during render.
//
// That is not a real crash: the app was simply updated underneath the tab. The
// correct recovery is a single full-page reload, which fetches a fresh
// index.html + the current chunk names. This module centralizes (a) detecting
// that specific error shape and (b) performing a ONE-TIME, loop-guarded reload.

const RELOAD_GUARD_KEY = "veras:stale-chunk-reload-at";

// If a reload was attempted within this window and we still hit a stale-chunk
// error, the reload did NOT fix it (a genuinely broken deploy, or the missing
// chunk is served but still failing). Stop auto-reloading and let the caller
// fall through to the maintenance screen so the user is never trapped in an
// endless refresh loop. A later stale-chunk (minutes after, from a subsequent
// deploy) is outside this window and will auto-reload again as expected.
const RELOAD_GUARD_WINDOW_MS = 10_000;

/**
 * True when `error` looks like a failed dynamic import of an outdated chunk
 * (i.e. "this tab is running a version that no longer exists on the server"),
 * rather than a genuine render/logic crash. Matches the ChunkLoadError name and
 * the browser-specific dynamic-import failure messages across Chrome/Firefox/
 * Safari, plus Vite's own preload-error wording.
 */
export function isStaleChunkError(error: unknown): boolean {
  if (error == null) return false;

  const name =
    typeof error === "object" && error !== null && "name" in error
      ? String((error as { name?: unknown }).name ?? "")
      : "";
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : typeof error === "object" && error !== null && "message" in error
          ? String((error as { message?: unknown }).message ?? "")
          : String(error);

  const haystack = `${name} ${message}`.toLowerCase();

  return (
    name === "ChunkLoadError" ||
    haystack.includes("chunkloaderror") ||
    // Chrome / Vite
    haystack.includes("failed to fetch dynamically imported module") ||
    // Firefox
    haystack.includes("error loading dynamically imported module") ||
    // Safari
    haystack.includes("importing a module script failed") ||
    haystack.includes("unable to preload") ||
    // Generic fallbacks
    haystack.includes("dynamically imported module") ||
    // MIME-type variant: when a hashed chunk 404s, the SPA server answers the
    // request with index.html (text/html) instead of JS, so the browser refuses
    // the module script. This wording contains none of the phrases above, so it
    // used to slip through to the maintenance screen. Chrome/Edge emit "Failed
    // to load module script: Expected a JavaScript module script but the server
    // responded with a MIME type of \"text/html\"…"; Firefox/Safari emit close
    // variants ("Expected a JavaScript module script…", "not a valid JavaScript
    // MIME type"). Match all three shapes. (Task #3085.)
    haystack.includes("failed to load module script") ||
    haystack.includes("expected a javascript module script") ||
    haystack.includes("is not a valid javascript mime type") ||
    // Firefox's phrasing of the same MIME rejection.
    haystack.includes("disallowed mime type") ||
    // Webpack-style chunk failure wording ("Loading chunk 123 failed"), in case
    // the ChunkLoadError name was stripped somewhere in the capture path.
    haystack.includes("loading chunk") ||
    // React.lazy() failed-import variant: every route is a content-hashed chunk
    // loaded via React.lazy(() => import(...)), and lazy reads `.default` off the
    // resolved module. In most browsers a stale-chunk import rejects cleanly and
    // is caught by the phrases above, but in the case where the failed import
    // resolves to `undefined` instead of rejecting, React tries to read
    // `.default` on `undefined` and throws a TypeError whose wording contains
    // none of them — so it used to slip through to the maintenance screen and
    // fire a Sentry alert. Match it TIGHTLY on the `.default` access so unrelated
    // undefined-property crashes are NOT swallowed; the one-time loop guard is
    // the safety net if a genuine `.default` logic bug ever matches. (Task #3136.)
    //
    // Chrome / V8 (current): "Cannot read properties of undefined (reading 'default')"
    haystack.includes("reading 'default'") ||
    // Chrome / V8 (older): "Cannot read property 'default' of undefined"
    haystack.includes("'default' of undefined") ||
    // Safari: "undefined is not an object (evaluating 'x.default')"
    (haystack.includes("undefined is not an object") &&
      haystack.includes(".default'")) ||
    // Firefox: "can't access property \"default\", x is undefined"
    haystack.includes('access property "default"')
  );
}

/**
 * Attempt a single, loop-guarded full-page reload to recover from a stale
 * chunk. Returns `true` if a reload was triggered (the page is now navigating
 * away), or `false` if a reload was suppressed — either because one was already
 * attempted within {@link RELOAD_GUARD_WINDOW_MS} (loop guard) or because
 * `sessionStorage` is unavailable (fail safe: do not risk an unguarded loop).
 *
 * When this returns `false`, the caller should fall through to the maintenance
 * screen so a genuinely broken deploy surfaces instead of refreshing forever.
 */
export function attemptStaleChunkReload(): boolean {
  let store: Storage | null = null;
  try {
    store = window.sessionStorage;
  } catch {
    store = null;
  }

  // Without a durable guard we cannot prove a reload isn't already looping, so
  // fail safe and do NOT reload.
  if (!store) return false;

  try {
    const now = Date.now();
    const raw = store.getItem(RELOAD_GUARD_KEY);
    const last = raw ? Number(raw) : 0;
    if (last && Number.isFinite(last) && now - last < RELOAD_GUARD_WINDOW_MS) {
      // Already reloaded very recently and still broken — stop looping.
      return false;
    }
    store.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    return false;
  }

  window.location.reload();
  return true;
}
